// Booking related models and interfaces

import type { Dayjs } from 'dayjs';

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface ApiErrorResponse {
  success: false;
  status?: string;
  statusCode?: number;
  code?: string;
  errorCode?: string;
  message?: string;
}

// Enums
export const Gender = {
  Male: 1,
  Female: 2,
  LGBTQIA: 8,
  Any: 9,
} as const;

export type Gender = (typeof Gender)[keyof typeof Gender];

export const PaymentMethodType = {
  Empty: 0,
  // Backend now uses 1 = Online; keep `Package` as alias for backward compatibility.
  Package: 1,
  Online: 1,
  BankTransfer: 2,
  Promptpay: 3,
  CreditCard: 4,
  Alipay: 5,
} as const;

export type PaymentMethodType =
  (typeof PaymentMethodType)[keyof typeof PaymentMethodType];

export const PackageType = {
  CashWallet: 1,
  Times: 2,
  Minutes: 3,
  Percent: 4,
} as const;

export type PackageType = (typeof PackageType)[keyof typeof PackageType];

export const DiscountUnitType = {
  // Backend contract: 1 = Percent, 2 = Amount.
  Percent: 1,
  Amount: 2,
} as const;

export type DiscountUnitType =
  (typeof DiscountUnitType)[keyof typeof DiscountUnitType];

export const BookingStatusCode = {
  Confirmed: 1,
  Canceled: 2,
  Completed: 3,
} as const;

export type BookingStatusCode =
  (typeof BookingStatusCode)[keyof typeof BookingStatusCode];

export const PaymentStatusCode = {
  Unpaid: 0,
  Paid: 1,
  PartiallyPaid: 2,
  Refunded: 3,
  PartiallyRefunded: 4,
  Canceled: 5,
} as const;

export type PaymentStatusCode =
  (typeof PaymentStatusCode)[keyof typeof PaymentStatusCode];

export const DepositMode = {
  Percent: 'percent',
  Fixed: 'fixed',
  PerCustomer: 'perCustomer',
} as const;

export type DepositMode = (typeof DepositMode)[keyof typeof DepositMode];

// Core models from API
export interface Brand {
  brandId: number;
  name: string;
  branchCount: number;
  branches: Store[];
  bannerImageUrl: string;
  logoImageUrl: string;
  primaryColor: string;
  secondaryColor: string;
  liffId?: string;
  termsAndConditions: {
    en: string;
    th: string;
  };
  cancellationPolicy: {
    en: string;
    th: string;
  };
  showTermsAndConditions: boolean;
  showCancellationPolicy: boolean;
  defaultLanguage: 'th' | 'en';
  maxAdvanceBookingDays: number;
  paymentType: PaymentType;
  depositMode: DepositMode;
}

export interface Store {
  storeId: number;
  name: string;
  branchName: string;
  maxAdvanceBookingDays: number;
  maxCustomerCount: number;
  paymentType: PaymentType;
  showPackageUsage: boolean;
  depositMode: DepositMode;
}

// Extracted payment type for Brand/Store
export const PaymentType = {
  None: 'none',
  Deposit: 'deposit',
  Full: 'full',
} as const;

export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType];

export interface Availability {
  availabilityId: string; // ISO8601 string with timezone
  available: boolean;
}

export interface Treatment {
  treatmentId: number;
  name: string;
  description: string;
  remark: string;
  imageUrl: string;
  startingPrice: number;
  available: boolean;
  options: Option[];
  categoryName: string;
}

export interface Option {
  optionId: number;
  duration: number;
  originalPrice: number;
  retailPrice: number;
  paxCount: number;
  available: boolean;
  suggestion: string;
}

export interface Therapist {
  therapistId: number;
  name: string;
  gender: Gender;
}

export interface Customer {
  name: string;
  telPrefix: string;
  tel: string;
  email: string;
  remark: string;
  useContactInfo: boolean;
  therapistPreference: {
    gender: Gender;
    preferredTherapistIds: number[];
    unpreferredTherapistIds: number[];
  };
  treatmentItems: TreatmentItem[];
}

export interface TreatmentItem {
  treatmentId: number;
  optionId: number;
  partnerCustomerIndex: number;
  originalPrice: number;
  retailPrice: number;
  // Returned when fetching booking details (e.g. Thank You page)
  treatmentName?: string;
}

export interface Payment {
  amount: number;
  method: string;
  paidAt: string;
}

// Booking model - main booking interface
export interface Booking {
  bookingCode?: string;
  customers: Customer[];
  availabilityId: string;
  status?: BookingStatusCode;
  paymentStatus?: PaymentStatusCode;
  storeId: number;
  depositTotal: number;
  remainingTotal: number;
  storeName?: string;
  storeBranchName?: string;
  name?: string;
  telPrefix?: string;
  tel?: string;
  email?: string;
  paymentMethod?: PaymentMethodType;
  payments?: Payment[];
  // Some booking APIs also return balance breakdown.
  promotions?: Promotion[];
  promotionDiscount?: number;
  packageDiscount?: number;
  subtotal?: number;
  total?: number;
  totalDiscount?: number;
}

export interface BookingWithPackageUsage extends Booking {
  packageUsageAmount: number;
}

export interface CustomerPackage {
  id: number;
  name: string;
  type: PackageType;
  expiryDate: Date;
  remainingValue: number;
  totalValue: number;
}

export type CustomerPackageStatusCode =
  | 'PENDING_PAYMENT_PACKAGE_USAGE'
  | 'NO_PACKAGE';

export interface CustomerPackageStatus {
  code: CustomerPackageStatusCode;
}

// Package validation / discount calculation models
export interface SelectedPackage {
  id: number; // customerPackage.id
  value: number; // user entered value (amount, times, minutes, or percent implied)
  // Frontend-only metadata for UI rendering.
  name?: string;
  type?: PackageType;
  remainingValue?: number;
  totalValue?: number;
  // Returned from validation endpoints when the backend adjusts a selected value.
  previousValue?: number;
  // Returned from validation endpoints to indicate whether the selected value is valid.
  valid?: boolean;
}

export interface Promotion {
  id: number;
  name: string;
  discountAmount: number;
  discountValue: number;
  discountUnit: DiscountUnitType;
}

export interface ValidatePackagesResponse extends Balance {
  valid: boolean;
  selectedPackages: SelectedPackage[];
}

// Customer packages validation request/response (frontend naming convenience)
export interface ValidateCustomerPackagesRequest {
  selectedPackages: SelectedPackage[];
  customers: Customer[];
}

export interface GetCustomerPackagesStatusRequest {
  phoneNumber: string;
  ref: string;
}

// OTP related types
export interface OTPResponse {
  ref: string;
  expiresAt: Date;
  code?: string; // only available in dev mode
}

export interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
}

// QR Payment Status (PromptPay, etc.)
export type QrPaymentStatusValue = 'unpaid' | 'paid' | 'expired';

export interface QrPaymentStatus {
  bookingCode: string;
  status: QrPaymentStatusValue;
  /**
   * Expiry timestamp from backend. Currently handled as string (ISO) to avoid premature parsing;
   * convert to Date where needed in UI logic.
   */
  expiresAt: string;
}

// Booking API request/response types
export interface CheckAvailabilityRequest {
  customerCount: number;
  localDate: string; // YYYY-MM-DD format
  availabilityId?: string; // ISO8601 string with timezone
  customers?: Customer[];
}

export interface ListTreatmentsRequest {
  customers: Customer[];
  customerIndex: number;
  availabilityId: string;
}

export interface ChooseTreatmentRequest {
  customers: Customer[];
  customerIndex: number;
  treatmentId: number;
  optionId: number;
  isAssignedToAll: boolean;
  partnerCustomerIndex?: number;
  availabilityId: string;
  selectedPackages?: SelectedPackage[];
}

export interface DeleteTreatmentRequest {
  customers: Customer[];
  customerIndex: number;
  treatmentItemIndex: number; // -1 to remove all items from given customer index
  availabilityId: string;
  selectedPackages?: SelectedPackage[];
}

export interface ValidateBookingRequest {
  customers: Customer[];
  availabilityId: string;
  selectedPackages?: SelectedPackage[];
}

export interface CreateBookingRequest {
  customers: Customer[];
  availabilityId: string;
  name: string;
  telPrefix: string;
  tel: string;
  email: string;
  lineUserId?: string;
  selectedPackages?: SelectedPackage[];
  paymentMethod: PaymentMethodType;
}

export interface ChooseTreatmentResponse extends Balance {
  customers: Customer[];
}

export interface DeleteTreatmentResponse extends Balance {
  customers: Customer[];
}

export interface ValidateBookingResponse extends Balance {
  customers: Customer[];
}

export interface Balance {
  promotions: Promotion[];
  promotionDiscount: number;
  packageDiscount: number;
  subtotal: number;
  total: number;
  totalDiscount: number;
  depositTotal: number;
  depositValue: number;
  remainingTotal: number;
}

export interface CreateBookingResponse {
  bookingCode: string;
  calendarData: string;
  paymentQrData: string;
  paymentQrImageBase64: string;
  paymentUrl: string;
  paymentQrExpiresAt?: string;
}

// Router location state passed between payment pages (PromptPay, etc.)
export interface BookingPaymentLocationState {
  bookingCode: string;
  paymentQrImageBase64?: string;
  paymentQrData?: string;
  paymentQrExpiresAt?: string; // ISO string from backend
}

// Calendar event parameters
export interface CalendarEventParams {
  brandName: string;
  branchName: string;
  translations: {
    bookingCodeLabel: string;
    customer: string;
    yourAppointment: string;
    guests: string;
    treatmentFallback: string;
    minutesShort: string;
  };
}

// Booking state interface for the store
export interface BookingState {
  // Step 1: Date & Time
  storeId: number; // Store ID
  numberOfGuests: number;
  availabilityId: string; // ISO8601 string containing both date and time

  // Step 2: Treatment
  customers: Customer[];
  availableTreatments: Treatment[]; // Cache of available treatments for price calculation

  // Step 3: Payment
  name: string;
  telPrefix: string;
  tel: string;
  email: string;
  paymentMethod: PaymentMethodType;
  useContactInfoForCustomers: boolean;
  // Selected packages applied to booking (validated list)
  selectedPackages: SelectedPackage[];

  balance: Balance;

  bookingCode: string;

  // Actions
  setStoreId: (storeId: number) => void;
  setAvailabilityId: (availabilityId: string) => void;

  setNumberOfGuests: (count: number) => void;
  addCustomer: () => void;
  removeCustomer: (customerIndex: number) => void;
  updateCustomer: (
    customerIndex: number,
    customerData: Partial<Customer>,
  ) => void;
  clearAllTreatments: () => void;
  setAvailableTreatments: (treatments: Treatment[]) => void;
  // Direct setters for flattened contact fields
  setContactInfo: (
    data: Partial<Pick<BookingState, 'name' | 'telPrefix' | 'tel' | 'email'>>,
  ) => void;
  setPaymentMethod: (method: PaymentMethodType) => void;
  setUseContactInfoForCustomers: (use: boolean) => void;
  setSelectedPackages: (packages: SelectedPackage[]) => void;
  setBalanceSummary: (data: Balance) => void;
  setBookingCode: (code: string) => void;
  reset: () => void;

  // Getters
  getMaxDuration: () => number;
  getTotalTreatments: () => number;
  getStartTime: () => Dayjs | null;
  getEndTime: () => Dayjs | null;
  isStep1Complete: () => boolean;
  isStep2Complete: () => boolean;
  hasMissingTreatments: () => boolean;
  hasAnyTreatments: () => boolean;
  isStep3Complete: () => boolean;

  // Calendar
  downloadCalendarEvent: (params: CalendarEventParams) => void;
}
